import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import { getQuoteText } from '@utils/representativeQuotes';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GroupIcon from '@mui/icons-material/Group';
import LabelIcon from '@mui/icons-material/Label';
import MapIcon from '@mui/icons-material/Map'; // Example Icon for JTBD
import NumbersIcon from '@mui/icons-material/Numbers';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useParams } from 'react-router';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------*/
const getJobTypeStyle = (type) => {
  switch (type?.toLowerCase()) {
    case 'functional':
      return { color: 'primary', label: 'Functional', icon: <BuildIcon fontSize="small" /> };
    case 'emotional':
      return {
        color: 'secondary',
        label: 'Emotional',
        icon: <FavoriteBorderIcon fontSize="small" />,
      };
    case 'social':
      return { color: 'success', label: 'Social', icon: <GroupIcon fontSize="small" /> };
    default:
      return { color: 'default', label: type || 'Unknown', icon: <LabelIcon fontSize="small" /> };
  }
};

/** -------------------------------------------------------------
 * JtbdMapAnalysis – displays a portfolio-level JTBD Map analysis
 * ------------------------------------------------------------*/
export default function JtbdMapAnalysis({ item }) {
  const [expanded, setExpanded] = React.useState(false);
  const { id } = useParams();

  if (!item) return null;

  // --- Start: Inline data handling logic ---
  let results = {};
  if (typeof item.results === 'object' && item.results !== null) {
    results = item.results; // Use directly if already an object
  } else if (typeof item.results === 'string') {
    try {
      results = JSON.parse(item.results); // Try parsing if it's a string
    } catch (err) {
      console.error('Failed to parse results JSON string:', err, 'JSON String:', item.results);
      results = {}; // Assign empty object on parsing error
    }
  } else {
    // Handle cases where results is neither object nor string (e.g., undefined, null)

    console.warn('Results field is not an object or string:', item.results);
  }
  // --- End: Inline data handling logic ---

  // Fallback if results are invalid or missing the required 'summary'
  if (Object.keys(results).length === 0 || !results.summary) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Analysis Data Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The results for this JTBD Map analysis could not be loaded or are improperly formatted.
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Show Raw Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(item, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }

  /* ──────────────────────── Job Prevalence Accordions ─────────────────────── */
  const JobPrevalence = () => {
    if (!results.job_prevalence?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    };

    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Job Breakdown
        </Typography>
        {results.job_prevalence.map((job, index) => {
          const panelId = `job-panel-${index}`;
          const jobStyle = getJobTypeStyle(job.job_type);
          return (
            <Accordion
              key={panelId}
              expanded={expanded === panelId}
              onChange={handleChange(panelId)}
              sx={{ mb: 1, overflow: 'auto' }}
              variant="outlined"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{
                  overflow: 'hidden',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                  {job.job}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={jobStyle.icon}
                    label={jobStyle.label}
                    size="small"
                    color={jobStyle.color}
                    variant="outlined"
                  />
                  <Chip
                    icon={<NumbersIcon />}
                    label={`Prevalence: ${job.prevalence}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Grid container spacing={3}>
                  {/* Common Triggers */}
                  {job.common_triggers?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <PlayCircleOutlineIcon fontSize="small" color="action" /> Common Triggers
                      </Typography>
                      <List dense disablePadding>
                        {job.common_triggers.map((trigger, i) => (
                          <ListItem key={i} disableGutters sx={{ pl: 1 }}>
                            <ListItemText
                              primary={trigger}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}

                  {/* Common Struggles */}
                  {job.common_struggles?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <ReportProblemIcon fontSize="small" color="warning" /> Common Struggles
                      </Typography>
                      <List dense disablePadding>
                        {job.common_struggles.map((struggle, i) => (
                          <ListItem key={i} disableGutters sx={{ pl: 1 }}>
                            <ListItemText
                              primary={struggle}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}

                  {/* Common Desired Outcomes */}
                  {job.common_desired_outcomes?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CheckCircleOutlineIcon fontSize="small" color="success" /> Common Desired
                        Outcomes
                      </Typography>
                      <List dense disablePadding>
                        {job.common_desired_outcomes.map((outcome, i) => (
                          <ListItem key={i} disableGutters sx={{ pl: 1 }}>
                            <ListItemText
                              primary={outcome}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}

                  {/* Representative Quotes */}
                  {job.representative_quotes?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                      </Typography>
                      <List dense disablePadding sx={{ pl: 1, overflow: 'auto' }}>
                        {job.representative_quotes.map((quote, i) => (
                          <ListItem key={i} disableGutters>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <FormatQuoteIcon
                                sx={{
                                  fontSize: 16,
                                  transform: 'scaleX(-1)',
                                  color: 'text.disabled',
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={getQuoteText(quote)}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontStyle: 'italic',
                                color: 'text.secondary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <AnalysisLayout
      results={results}
      item={{ ...item, name: 'Jobs To Be Done (JTBD) Map Analysis' }}
    >
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <MapIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Jobs To Be Done (JTBD) Map Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />
        <PriorityInsights results={results} />
        <JobPrevalence />
        <IncludedTranscripts item={item} id={id} />
      </CardContent>
    </AnalysisLayout>
  );
}

JtbdMapAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
